/**
 * Goal authorship badge + edit rules, shared by the student / parent / teacher
 * program views.
 * Web (dijipo-web-main/…/src/utils/goalAuthor.ts) and mobile (dijipo-mobile/utils/goalAuthor.ts)
 * keep byte-for-byte identical copies — change both.
 *
 * `student_goals.created_by_type` has no 'admin' member, so a kurum/admin
 * "Toplu Hedef Atama" writes 'system'. Before that existed every call site did
 * a bare `created_by_type === 'teacher'` test, which quietly bucketed a
 * kurum-assigned goal as the student's own — mislabelling it AND handing the
 * student a delete button for it. The rule is now: anything that is not
 * 'student' was assigned from above and the student may not remove it.
 *
 * A teacher's goal names its teacher (2026-10, `created_by_name` from the API)
 * instead of a generic "Öğretmen Hedefi": a student can have several teachers,
 * and a teacher planning a time slot needs to see whose goal already holds it.
 */
export type GoalAuthor = string | undefined | null;

export const isSelfSetGoal = (t: GoalAuthor): boolean => t === 'student' || t == null;

export interface GoalBadge {
  text: string;
  /** Hex for inline styles (web). */
  bg: string;
  fg: string;
  /** The same colours as literal Tailwind classes (mobile, NativeWind). */
  bgClass: string;
  fgClass: string;
}

export interface GoalAuthorLike {
  created_by_type?: GoalAuthor;
  created_by_name?: string | null;
}

export const goalAuthorBadge = (g: GoalAuthorLike): GoalBadge => {
  const t = g.created_by_type;
  if (t === 'teacher' || t === 'coach') {
    const name = g.created_by_name?.trim();
    return { text: name || 'Öğretmen Hedefi', bg: '#f5c518', fg: '#1a1a1a', bgClass: 'bg-[#f5c518]', fgClass: 'text-[#1a1a1a]' };
  }
  if (t === 'system') return { text: 'Kurum Hedefi', bg: '#7b5cd6', fg: '#ffffff', bgClass: 'bg-[#7b5cd6]', fgClass: 'text-white' };
  return { text: 'Öğrenci Hedefi', bg: '#3973bf', fg: '#ffffff', bgClass: 'bg-[#3973bf]', fgClass: 'text-white' };
};
